import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { hasAnyPermission, hasPermission } from "../../../utils/permissions";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";

interface GeneralInfoModel {
  id?: number;
  youTubeVideoLink: string;
  phoneNumber: string;
  email: string;
  address: string;
}

export const GeneralInfo: React.FC = () => {
  const { i18n } = useTranslation(["common"]);
  const isRTL = i18n.language === "ar";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<GeneralInfoModel>({
    youTubeVideoLink: "",
    phoneNumber: "",
    email: "",
    address: ""
  });

  const canView = hasAnyPermission("View General Info", "Update General Info");
  const canUpdate = hasPermission("Update General Info");

  const fetchGeneralInfo = async () => {
    try {
      setLoading(true);
      const response = await adminApi.call("/generalinfo", "GET");

      if (!response.success) {
        showErrorToast(response.error || "Failed to load general info");
        return;
      }

      const data = response.data || {};
      setFormData({
        id: data.id,
        youTubeVideoLink: data.youTubeVideoLink || "",
        phoneNumber: data.phoneNumber || "",
        email: data.email || "",
        address: data.address || ""
      });
    } catch (error) {
      showErrorToast("Error loading general info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) {
      fetchGeneralInfo();
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!canUpdate) {
      return;
    }

    setSaving(true);
    try {
      const response = await adminApi.call("/generalinfo", "PUT", {
        youTubeVideoLink: formData.youTubeVideoLink,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        address: formData.address
      });

      if (!response.success) {
        showErrorToast(response.error || "Failed to update general info");
        return;
      }

      showSuccessToast("General info updated successfully");
      await fetchGeneralInfo();
    } catch (error) {
      showErrorToast("Error updating general info");
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return (
      <PageLayout title="General Info" subtitle="CMS" breadcrumbs={[{ label: "CMS" }, { label: "General Info" }]}>
        <div className="alert alert-danger mb-0">You do not have permission to view this page.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="General Info"
      subtitle="Manage website general information"
      breadcrumbs={[{ label: "CMS" }, { label: "General Info" }]}
      className={isRTL ? "rtl" : "ltr"}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        {loading ? (
          <div className="text-center py-4">Loading general info...</div>
        ) : (
          <div className="row g-4">
            <div className="col-12">
              <label className="form-label">YouTube Video Link</label>
              <input
                type="url"
                className="form-control"
                name="youTubeVideoLink"
                value={formData.youTubeVideoLink}
                onChange={handleInputChange}
                placeholder="https://www.youtube.com/embed/..."
                disabled={!canUpdate}
              />
              <div className="form-text">
                This link is used on the public website video section.
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Phone Number</label>
              <input
                type="text"
                className="form-control"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                disabled={!canUpdate}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!canUpdate}
              />
            </div>

            <div className="col-12">
              <label className="form-label">Address</label>
              <textarea
                className="form-control"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows={3}
                disabled={!canUpdate}
              />
            </div>
          </div>
        )}

        <div className="text-center mt-4">
          <ActionButton
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!canUpdate || saving || loading}
          >
            Save
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
